import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

import { TaggedPackagesTable } from './components/tagged-packages-table.js';

const searchSchema = z.object({
	tagIds: z.array(z.string()).optional(),
});

export const Route = createFileRoute('/_authorization-gate/package-tags/')({
	staticData: {
		authorize: (ability) => ability.can('manage', 'tag'),
		navigation: {
			label: 'Package Tags',
			keywords: ['tags', 'labels'],
		},
	},
	validateSearch: searchSchema,
	beforeLoad: ({ context: { trpc } }) => {
		return {
			getTagsOptions: trpc.tag.getMany.queryOptions({ withPackageCount: true }),
			getTaggedPackagesOptions: trpc.tag.getAllTaggedPackages.queryOptions({}),
		};
	},
	loader: async ({ context: { queryClient, getTagsOptions, getTaggedPackagesOptions } }) => {
		await Promise.all([
			queryClient.ensureQueryData(getTagsOptions),
			queryClient.ensureQueryData(getTaggedPackagesOptions),
		]);
	},
	head: () => ({ meta: [{ title: 'Tags • Cadstrom Offline HIL' }] }),
	component: TagsPage,
});

function TagsPage() {
	const { tagIds } = Route.useSearch();
	const navigate = useNavigate();

	const { getTagsOptions, getTaggedPackagesOptions } = Route.useRouteContext();

	const {
		data: { tags },
	} = useSuspenseQuery(getTagsOptions);
	const {
		data: { packages },
	} = useSuspenseQuery(getTaggedPackagesOptions);

	const handleTagFilterChange = (newTagIds: string[]) => {
		void navigate({
			to: '/package-tags',
			search: newTagIds.length > 0 ? { tagIds: newTagIds } : {},
		});
	};

	return (
		<div className='flex h-full flex-col p-4'>
			<h1 className='mb-4 text-xl font-semibold'>Tagged Packages</h1>
			<div className='min-h-0 flex-1'>
				<TaggedPackagesTable
					tags={tags}
					packages={packages}
					tagFilter={tagIds ?? []}
					onTagFilterChange={handleTagFilterChange}
				/>
			</div>
		</div>
	);
}
